import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getJSON, postJSON } from '../../api/client.js';
import {
  CrudPage, SpecCreateModal, THEME, UniActionButton, UniPageShell,
  UniStatusPill, UniTable, WABA_API, useUniList, useUniCreate,
} from './WabaShared.jsx';

const NODE_TYPES = [
  'trigger', 'send_message', 'send_template', 'send_interactive', 'send_list',
  'condition', 'wait_delay', 'wait_for_reply', 'assign_agent', 'set_tag',
  'close_conversation', 'opt_out', 'call_api',
];

const FLOW_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'trigger_type', label: 'Trigger type' },
  { key: 'trigger_keywords', label: 'Keywords', hint: 'JSON list or comma-separated' },
  { key: 'priority', label: 'Priority', type: 'number' },
  { key: 'is_active', label: 'Active', type: 'check' },
];

export function WabaFlows() {
  return (
    <CrudPage
      theme={THEME}
      api={WABA_API}
      helpPage="flows"
      icon="🔀"
      title="Bot Studio"
      home="/waba-chat"
      endpoint={`${WABA_API}/flows/`}
      fields={FLOW_FIELDS}
      searchPlaceholder="Search flows…"
      createLabel="+ New flow"
      empty="No flows yet"
      emptyHint="Create a keyword or button flow. Higher priority wins before RAG."
      columns={[
        { key: 'name', label: 'Flow', render: (r) => <strong>{r.name}</strong> },
        { key: 'trigger_type', label: 'Trigger' },
        { key: 'priority', label: 'Priority', align: 'right' },
        { key: 'is_active', label: 'Active', render: (r) => <UniStatusPill value={r.is_active ? 'active' : 'off'} /> },
        {
          key: 'actions',
          label: 'Actions',
          align: 'right',
          render: (r) => (
            <a className="uni-row-btn" href={`/waba-chat/flows/${r.id}`}>Open builder</a>
          ),
        },
      ]}
    />
  );
}

export function WabaFlowBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const flowUrl = `${WABA_API}/flows/${id}/`;
  const nodesQ = useUniList(`${WABA_API}/flow-nodes/?flow=${id}`, [id]);
  const edgesQ = useUniList(`${WABA_API}/flow-edges/?flow=${id}`, [id]);
  const flowQ = useUniList(flowUrl, [id]);
  const createNode = useUniCreate(`${WABA_API}/flow-nodes/`, {
    flow: Number(id), node_type: 'send_message', name: 'New node',
    position_x: 40, position_y: 40, config: '{"text":"Hello"}',
  }, () => nodesQ.reload());
  const createEdge = useUniCreate(`${WABA_API}/flow-edges/`, {
    flow: Number(id), from_node: '', to_node: '', branch: '',
  }, () => edgesQ.reload());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggle = async () => {
    setBusy(true);
    try {
      await postJSON(`${flowUrl}toggle/`, {});
      flowQ.reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const copy = await postJSON(`${flowUrl}duplicate/`, {});
      if (copy?.id) navigate(`/waba-chat/flows/${copy.id}`);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const flow = flowQ.raw && !Array.isArray(flowQ.raw) ? flowQ.raw : null;
  const nodes = nodesQ.rows;
  const edges = edgesQ.rows;

  return (
    <UniPageShell
      theme={THEME}
      icon="🔀"
      title={flow?.name || 'Flow builder'}
      subtitle="Nodes + edges. The engine walks these on inbound WhatsApp events before RAG."
      helpPage="flows"
      helpApi={WABA_API}
      actions={(
        <>
          <UniActionButton hero primary onClick={createNode.start}>+ Node</UniActionButton>
          <UniActionButton hero onClick={createEdge.start}>+ Edge</UniActionButton>
          <UniActionButton hero onClick={toggle} disabled={busy}>
            {flow?.is_active ? 'Deactivate' : 'Activate'}
          </UniActionButton>
          <UniActionButton hero onClick={duplicate} disabled={busy}>Duplicate</UniActionButton>
          <UniActionButton hero to="/waba-chat/flows">← Flows</UniActionButton>
        </>
      )}
    >
      {msg && <div className="ty-error">{msg}</div>}
      <div className="waba-flow-canvas">
        {nodes.map((n) => (
          <div
            key={n.id}
            className="waba-flow-node"
            style={{ left: n.position_x || 20, top: n.position_y || 20 }}
          >
            <strong>{n.name || n.node_type}</strong>
            <div className="uni-num--muted">{n.node_type} · #{n.id}</div>
          </div>
        ))}
        {nodes.length === 0 && (
          <div className="uni-state"><div className="uni-state__title">No nodes yet</div></div>
        )}
      </div>
      <h3 style={{ marginTop: 18 }}>Edges</h3>
      <UniTable
        rows={edges}
        loading={edgesQ.loading}
        columns={[
          { key: 'from_node', label: 'From' },
          { key: 'to_node', label: 'To' },
          { key: 'branch', label: 'Branch' },
        ]}
        empty="No edges"
      />
      <SpecCreateModal
        create={createNode}
        title="Add node"
        fields={[
          { key: 'flow', label: 'Flow ID', type: 'number', required: true },
          { key: 'name', label: 'Name' },
          { key: 'node_type', label: 'Type', type: 'select', options: NODE_TYPES.map((t) => ({ value: t, label: t })) },
          { key: 'position_x', label: 'X', type: 'number' },
          { key: 'position_y', label: 'Y', type: 'number' },
          { key: 'config', label: 'Config JSON', type: 'json' },
        ]}
      />
      <SpecCreateModal
        create={createEdge}
        title="Add edge"
        fields={[
          { key: 'flow', label: 'Flow ID', type: 'number', required: true },
          { key: 'from_node', label: 'From node id', type: 'number', required: true },
          { key: 'to_node', label: 'To node id', type: 'number', required: true },
          { key: 'branch', label: 'Branch (optional)' },
        ]}
      />
    </UniPageShell>
  );
}
